/**
 * Material, good, building, and drone-class data tables, transcribed
 * verbatim from colony-game-poc-spec.md. Pure data and types only — no
 * imports, no functions, no runtime logic.
 *
 * Array constants (`*_IDS`, `DRONE_CLASSES`) fix iteration order to match
 * the spec's table order, per the determinism rule in spec §11 rule 3
 * (no iteration over unordered collections).
 */

// --- §4.1 Mined minerals (8) -------------------------------------------------

export type MineralId =
  | 'hematite'
  | 'quartz'
  | 'bauxite'
  | 'chalcopyrite'
  | 'waterIce'
  | 'carbonate'
  | 'apatite'
  | 'sylvite';

export const MINERAL_IDS: readonly MineralId[] = [
  'hematite',
  'quartz',
  'bauxite',
  'chalcopyrite',
  'waterIce',
  'carbonate',
  'apatite',
  'sylvite',
] as const;

// --- §4.3 Elements (14 — see errata) ------------------------------------------
//
// SPEC DEVIATION. §4.3 lists 13 elements and omits F, but §4.1 gives apatite as
// Ca5(PO4)3F yielding 1 F. Adding F as a 14th element rather than dropping the
// yield, because dissociation must conserve mass: the Phase 4 conservation test
// asserts that elements out equal elements in, and a silently discarded F would
// need a permanent carve-out in that test. F joins Cl as an inert byproduct —
// no §4.4 recipe consumes either, so both simply accumulate. See SPEC-ERRATA.md.

export type ElementId =
  | 'Fe'
  | 'Si'
  | 'Al'
  | 'Cu'
  | 'Ca'
  | 'C'
  | 'O'
  | 'H'
  | 'S'
  | 'P'
  | 'K'
  | 'Cl'
  | 'N'
  | 'F';

export const ELEMENT_IDS: readonly ElementId[] = [
  'Fe',
  'Si',
  'Al',
  'Cu',
  'Ca',
  'C',
  'O',
  'H',
  'S',
  'P',
  'K',
  'Cl',
  'N',
  'F',
] as const;

/**
 * Element names, for anywhere a person reads rather than a formula.
 *
 * The ids stay the chemical symbols: they are what §4.3's dissociation table is
 * written in, they keep the state small, and a recipe reading `{ Fe: 4, C: 1 }`
 * is clearer than one reading `{ Iron: 4, Carbon: 1 }`. But a stores list is
 * read down a column by someone deciding what to sell, and "Fe" asks them to
 * translate while "Iron" does not.
 *
 * Spelled to IUPAC, which is where "sulfur" — already the spelling used
 * throughout this codebase — comes from, and which pairs it with "aluminium".
 */
export const ELEMENT_NAMES: Record<ElementId, string> = {
  Fe: 'Iron',
  Si: 'Silicon',
  Al: 'Aluminium',
  Cu: 'Copper',
  Ca: 'Calcium',
  C: 'Carbon',
  O: 'Oxygen',
  H: 'Hydrogen',
  S: 'Sulfur',
  P: 'Phosphorus',
  K: 'Potassium',
  Cl: 'Chlorine',
  N: 'Nitrogen',
  F: 'Fluorine',
};

/**
 * Elements no §4.4 recipe consumes. They accumulate as inert byproducts.
 * Surfaced separately so the inventory panel can de-emphasise them, and so a
 * future market layer has an obvious first thing to sell.
 */
export const INERT_ELEMENT_IDS: readonly ElementId[] = ['S', 'Cl', 'F'] as const;

// --- §4.4 Synthesized goods (7) -----------------------------------------------

export type GoodId =
  | 'steelBeam'
  | 'glassPane'
  | 'wiring'
  | 'concreteBlock'
  | 'water'
  | 'fertilizer'
  | 'topsoil';

export const GOOD_IDS: readonly GoodId[] = [
  'steelBeam',
  'glassPane',
  'wiring',
  'concreteBlock',
  'water',
  'fertilizer',
  'topsoil',
] as const;

// --- §10 Buildings (6) ---------------------------------------------------------

export type BuildingId =
  | 'habitation'
  | 'farm'
  | 'waterPlant'
  | 'powerPlant'
  | 'factoryDistrict'
  | 'recreation';

export const BUILDING_IDS: readonly BuildingId[] = [
  'habitation',
  'farm',
  'waterPlant',
  'powerPlant',
  'factoryDistrict',
  'recreation',
] as const;

// --- §7 Drone classes (3) --------------------------------------------------------

export type DroneClass = 'standard' | 'heavy';

export const DRONE_CLASSES: readonly DroneClass[] = ['standard', 'heavy'] as const;

// --- Mineral data table (spec §4.1) --------------------------------------------

export const MINERALS: Record<
  MineralId,
  {
    id: MineralId;
    label: string;
    formula: string;
    yields: Partial<Record<ElementId, number>>;
  }
> = {
  hematite: {
    id: 'hematite',
    label: 'Hematite',
    formula: 'Fe2O3',
    yields: { Fe: 2, O: 3 },
  },
  quartz: {
    id: 'quartz',
    label: 'Quartz',
    formula: 'SiO2',
    yields: { Si: 1, O: 2 },
  },
  bauxite: {
    id: 'bauxite',
    label: 'Bauxite',
    formula: 'Al2O3·2H2O',
    yields: { Al: 2, O: 5, H: 4 },
  },
  chalcopyrite: {
    id: 'chalcopyrite',
    label: 'Chalcopyrite',
    formula: 'CuFeS2',
    yields: { Cu: 1, Fe: 1, S: 2 },
  },
  waterIce: {
    id: 'waterIce',
    label: 'Water ice',
    formula: 'H2O',
    yields: { H: 2, O: 1 },
  },
  carbonate: {
    id: 'carbonate',
    label: 'Carbonate',
    formula: 'CaCO3',
    yields: { Ca: 1, C: 1, O: 3 },
  },
  apatite: {
    id: 'apatite',
    label: 'Apatite',
    formula: 'Ca5(PO4)3F',
    yields: { Ca: 5, P: 3, O: 12, F: 1 },
  },
  sylvite: {
    id: 'sylvite',
    label: 'Sylvite',
    formula: 'KCl',
    yields: { K: 1, Cl: 1 },
  },
};

// --- Goods data table (spec §4.4) -----------------------------------------------
//
// Soil is the only good whose recipe includes another good as an ingredient
// (1 Fertilizer), alongside its element recipe (4 Si + 2 Ca + 2 C).

export const GOODS: Record<
  GoodId,
  {
    id: GoodId;
    label: string;
    recipe: Partial<Record<ElementId, number>>;
    ingredients?: Partial<Record<GoodId, number>>;
    energy: number;
  }
> = {
  steelBeam: {
    id: 'steelBeam',
    label: 'Steel beams',
    recipe: { Fe: 4, C: 1 },
    energy: 8,
  },
  glassPane: {
    id: 'glassPane',
    label: 'Glass panes',
    recipe: { Si: 2, O: 4 },
    energy: 6,
  },
  wiring: {
    id: 'wiring',
    label: 'Wiring',
    recipe: { Cu: 3, Al: 1 },
    energy: 10,
  },
  concreteBlock: {
    id: 'concreteBlock',
    label: 'Concrete blocks',
    recipe: { Ca: 2, Si: 2, O: 6, H: 2 },
    energy: 4,
  },
  water: {
    id: 'water',
    label: 'Water',
    recipe: { H: 2, O: 1 },
    energy: 2,
  },
  fertilizer: {
    id: 'fertilizer',
    label: 'Fertilizer',
    recipe: { N: 2, H: 6, P: 1, K: 1 },
    energy: 14,
  },
  topsoil: {
    id: 'topsoil',
    label: 'Topsoil',
    recipe: { Si: 4, Ca: 2, C: 2 },
    ingredients: { fertilizer: 1 },
    energy: 6,
  },
};

// --- Buildings data table (spec §10) ---------------------------------------------
//
// Judgment call: spec §10 says buildings consume 5-15 energy/tick upkeep "by
// type" without giving per-building figures. Values below were assigned
// within that stated range as instructed, spread across the six buildings:
// habitation 8, farm 10, waterPlant 12, powerPlant 5, factoryDistrict 15,
// recreation 6. These are not literal spec values, unlike every other field
// in this file.

export const BUILDINGS: Record<
  BuildingId,
  {
    id: BuildingId;
    label: string;
    cost: Partial<Record<GoodId, number>>;
    output: { kind: string; amount: number };
    upkeep: number;
  }
> = {
  habitation: {
    id: 'habitation',
    label: 'Habitation',
    cost: { concreteBlock: 60, steelBeam: 40, glassPane: 20, water: 10 },
    output: { kind: 'livingSpace', amount: 400 },
    upkeep: 8,
  },
  farm: {
    id: 'farm',
    label: 'Farm',
    cost: { concreteBlock: 30, steelBeam: 20, topsoil: 40, water: 30 },
    output: { kind: 'food', amount: 200 },
    upkeep: 10,
  },
  waterPlant: {
    id: 'waterPlant',
    label: 'Water plant',
    cost: { steelBeam: 50, concreteBlock: 30, wiring: 15 },
    output: { kind: 'potableWater', amount: 500 },
    upkeep: 12,
  },
  powerPlant: {
    id: 'powerPlant',
    label: 'Power plant',
    cost: { steelBeam: 70, wiring: 40, glassPane: 30 },
    output: { kind: 'energy', amount: 300 },
    upkeep: 5,
  },
  factoryDistrict: {
    id: 'factoryDistrict',
    label: 'Factory district',
    cost: { steelBeam: 80, concreteBlock: 40, wiring: 30 },
    output: { kind: 'manufacturing', amount: 250 },
    upkeep: 15,
  },
  recreation: {
    id: 'recreation',
    label: 'Recreation',
    cost: { concreteBlock: 40, glassPane: 30, steelBeam: 20, water: 15 },
    output: { kind: 'amenity', amount: 300 },
    upkeep: 6,
  },
};

// --- Drone class data table (spec §7) ---------------------------------------------
//
// Mini drones are starting units (3 provided) with no build cost; cost is an
// empty object rather than omitted, to keep the field present and typed.

export const DRONE_CLASSES_DATA: Record<
  DroneClass,
  {
    class: DroneClass;
    capacity: number;
    speedKmPerTick: number;
    cost: Partial<Record<GoodId, number>>;
  }
> = {
  standard: {
    class: 'standard',
    capacity: 200,
    speedKmPerTick: 6,
    cost: { steelBeam: 40, wiring: 15 },
  },
  heavy: {
    class: 'heavy',
    capacity: 500,
    speedKmPerTick: 7,
    cost: { steelBeam: 120, wiring: 40, glassPane: 20 },
  },
};
